import {
  getRelativeBoundingClientRect,
  getAggregateBoundingClientRect,
  parseCSSLength,
} from "./frontendlib/dom_utils";
import { type } from "./frontendlib/utils";
import EventListener from "./frontendlib/event_listener";

const EDGE_TO_TYPE = {
  left: "leading",
  right: "trailing",
  top: "leading",
  bottom: "trailing",
};
const EDGE_TO_DIMENSION = {
  left: "width",
  right: "width",
  top: "height",
  bottom: "height",
};

// We currently require CTA containers to be prefixed with "CTA", but we're
// more liberal here to support some legacy templates.
const ctaContainerRegex = /\bcta\b/i;
const isCTAContainer = (containerID) => {
  return ctaContainerRegex.test(containerID);
};

class Container extends EventListener {
  static getEvents() {
    return [{ name: "beforeLayout", system: true }, { name: "onClick" }].map(
      (e) => {
        e.type = "container";
        return e;
      }
    );
  }

  static getActions() {
    return [{ name: "show" }, { name: "hide" }].map((a) => {
      a.type = "container";
      return a;
    });
  }

  /** @param {HTMLDivElement} el - The container element. */
  constructor(el, id = "") {
    super();
    this.el = el;
    this.el.classList.add("madlib-container");
    if (isCTAContainer(id)) {
      // We tag CTA containers to help us detect when clicks occur on CTAs.
      this.el.classList.add("cta-container");
    }
    // `parent` and `children` refer to other Containers.
    this.parent = null;
    this.children = [];

    this.fixedWidth = false;
    this.fixedHeight = false;

    this.css = null;
    this.constraints = null;
    this.anchors = [];
    this.content = null;

    this.layoutReady = null;
    this.contentReady = null;
    this.hidden = false;

    this.postLayoutTransform = null;

    this.addEventListeners();
    this.coordinate();
  }

  addEventListeners() {
    this.el.addEventListener("click", () => this.emit("onClick"));
  }

  coordinate() {
    this.on({
      hide: () => {
        Object.assign(this.el.style, {
          pointerEvents: "none",
          visibility: "hidden",
        });
      },
      show: () => {
        Object.assign(this.el.style, {
          pointerEvents: (this.css && this.css["pointer-events"]) || "auto",
          visibility: (this.css && this.css.visibility) || "visible",
        });
      },
    });
  }

  loadContent() {
    if (this.contentReady == null) {
      const promises = this.children.map((child) => child.loadContent());
      if (this.content != null) {
        promises.push(this.content.load());
      }

      this.contentReady = Promise.all(promises);
    }

    return this.contentReady;
  }

  layout() {
    if (this.layoutReady == null) {
      // NOTE(oscar): beforeLayout should probably only trigger at most once per
      // container.
      this.emit("beforeLayout");
    }
    if (this.layoutReady == null) {
      if (this.el.parentNode !== this.parent.el) {
        this.parent.el.appendChild(this.el);
      }

      if (this.constraints != null) {
        if (
          (this.constraints.left != null && this.constraints.right != null) ||
          this.constraints.width != null
        ) {
          this.fixedWidth = true;
        }

        if (
          (this.constraints.top != null && this.constraints.bottom != null) ||
          this.constraints.height != null
        ) {
          this.fixedHeight = true;
        }
      }

      // The child components do not layout properly if a transform is active.
      // So, let's reset the transform before we proceed, then apply it later.
      this.resetTransform();

      this.style();
      this.position();
      this.layoutReady = this.loadContent()
        .then(() => this.anchor())
        .then(() => this.match())
        .then(() => this.layoutContent())
        .then(() => this.center())
        .then(() => this.applyTransform());
    }

    return this.layoutReady;
  }

  style() {
    // The zIndex creates a stacking context for the contents of the container
    // element so that they don't interfere with other containers.
    Object.assign(this.el.style, { zIndex: 1 }, this.css);
  }

  position() {
    if (this.constraints == null) return;
    const styles = { position: "absolute" };
    ["top", "bottom", "left", "right", "width", "height"].forEach((prop) => {
      const value = this.constraints[prop];
      if (value != null && type(value) !== "object") {
        styles[prop] = value;
      }
    });
    Object.assign(this.el.style, styles);
  }

  resetTransform() {
    if (this.postLayoutTransform != null) {
      this.el.style.transform = "none";
    }
  }

  applyTransform() {
    if (this.postLayoutTransform != null) {
      this.el.style.transform = this.postLayoutTransform;
    }
  }

  anchor() {
    let pRect;
    const promises = this.anchors.map(({ edge: containerEdge, anchor }) => {
      const { distance, edge: anchorEdge } = this.constraints[containerEdge];
      const containerEdgeType = EDGE_TO_TYPE[containerEdge];
      const anchorEdgeType = EDGE_TO_TYPE[anchorEdge];
      const dimension = EDGE_TO_DIMENSION[containerEdge];
      // Block positioning until the anchor is ready.
      return anchor.layout().then(() => {
        if (pRect == null) {
          pRect = this.parent.el.getBoundingClientRect();
        }

        const rect = getRelativeBoundingClientRect(anchor.el);
        let offset;
        switch (`${containerEdgeType} ${anchorEdgeType}`) {
          case "leading leading":
            offset = rect[anchorEdge];
            break;
          case "trailing trailing":
            offset = -rect[anchorEdge];
            break;
          case "leading trailing":
            offset = pRect[dimension] + rect[anchorEdge];
            break;
          case "trailing leading":
            offset = pRect[dimension] - rect[anchorEdge];
            break;
        }

        const { value, units } = parseCSSLength(distance);
        switch (units) {
          case "px":
            offset += value;
            break;
          case "%":
            offset += (pRect[dimension] * value) / 100;
            break;
        }

        this.el.style[containerEdge] = `${offset}px`;
      });
    });
    return Promise.all(promises);
  }

  match() {
    if (
      this.constraints == null ||
      !(
        this.constraints.matchWidthToHeight ||
        this.constraints.matchHeightToWidth
      )
    ) {
      return;
    }

    const { width, height } = this.el.getBoundingClientRect();
    if (this.constraints.matchWidthToHeight) {
      this.el.style.width = `${height}px`;
    } else if (this.constraints.matchHeightToWidth) {
      this.el.style.height = `${width}px`;
    }
  }

  layoutContent() {
    if (this.invalidated) return;

    const promises = this.children.map((child) => child.layout());
    if (this.content != null) {
      promises.push(this.content.layout(this.el));
    }

    return Promise.all(promises).then(() => {
      // For containers that serve as wrappers for other containers (i.e. do not
      // have content), calculate the aggregate bounding rectangle for all
      // children and resize the container to match the rectangle. This allows
      // centering to work properly.
      if (this.content != null || (this.fixedWidth && this.fixedHeight)) return;
      const rect = getAggregateBoundingClientRect(
        this.children.map((child) => child.el)
      );
      const style = {};
      if (!this.fixedWidth) {
        style.width = `${rect.width}px`;
      }

      if (!this.fixedHeight) {
        style.height = `${rect.height}px`;
      }

      Object.assign(this.el.style, style);
    });
  }

  center() {
    if (
      this.constraints == null ||
      !(this.constraints.centerHorizontal || this.constraints.centerVertical)
    ) {
      return;
    }

    const pRect = this.parent.el.getBoundingClientRect();
    const rect = this.el.getBoundingClientRect();
    if (this.constraints.centerHorizontal) {
      this.el.style.left = `${(pRect.width - rect.width) / 2}px`;
    }

    if (this.constraints.centerVertical) {
      this.el.style.top = `${(pRect.height - rect.height) / 2}px`;
    }
  }

  invalidate() {
    this.invalidated = true;

    // NOTE(vsingh):
    // The following is a hack on top of a hack. We invalidate containers when
    // resetting layout, so that any asynchronous process kicked off
    // by those containers will have no further effect.
    // Unfortunately the invalidation hack has holes: there are still
    // cases where side effects are applied to the DOM element
    // associated with the container. Therefore, dissociate the
    // container and the DOM element immediately upon container
    // invalidation.
    this.el = document.createElement("div");
  }
}

export default Container;



// WEBPACK FOOTER //
// ./src/js/container.js